import { isAxiosError } from 'axios'

import { handleException } from 'clients/exception-handling'
import type { ApiClient } from 'clients/yogi-api'
import type {
  AddMemberPayload,
  BulkCreateAndSharePayload,
  BulkCreateAndShareResponse,
  BulkJobStatusEntry,
  ChildProject,
  ResetUserResponse,
  TeamMember,
  TeamMembersGroupedResponse,
  TeamMembersResponse,
} from 'features/settings/features/access/types'

const HUB_UUID_HEADER = 'X-Hub-UUID'

export class AccessService {
  constructor(private client: ApiClient) {}

  async fetchTeamMembers(
    proj_uuid: string,
    includeYogiStaff = false,
  ): Promise<TeamMember[]> {
    const { data } = await this.client.get<TeamMembersResponse>(
      '/project/team-members',
      { params: { proj_uuid, include_yogi_staff: includeYogiStaff } },
    )
    return data.members
  }

  async fetchTeamMembersGrouped(
    proj_uuid: string,
    includeYogiStaff = false,
  ): Promise<{
    members: TeamMember[]
    childProjects: ChildProject[]
    childRoles: Record<string, Record<string, string | null>>
  }> {
    const { data } = await this.client.get<TeamMembersGroupedResponse>(
      '/project/team-members-grouped',
      { params: { proj_uuid, include_yogi_staff: includeYogiStaff } },
    )
    return {
      members: data.members,
      childProjects: data.child_projects,
      childRoles: data.child_roles,
    }
  }

  async updateMemberRole(
    proj_uuid: string,
    email: string,
    admin: boolean,
  ): Promise<void> {
    await this.client.patch(
      '/project/role',
      { shared_email: email, admin },
      { params: { proj_uuid } },
    )
  }

  async shareOrUpdateMemberRole(
    proj_uuid: string,
    email: string,
    admin: boolean,
    hasExistingRow: boolean,
  ): Promise<void> {
    if (hasExistingRow) {
      await this.updateMemberRole(proj_uuid, email, admin)
    } else {
      await this.client.post(
        '/project/share',
        { shared_email: email, admin, create_user: false },
        { params: { proj_uuid } },
      )
    }
  }

  async removeMember(proj_uuid: string, email: string): Promise<void> {
    await this.client.delete('/project/unshare', {
      params: { proj_uuid, shared_email: email },
    })
  }

  async checkUserExists(email: string): Promise<boolean> {
    const { data } = await this.client.get<{ user_exists: boolean }>(
      '/user/exists',
      { params: { email } },
    )
    return data.user_exists
  }

  /**
   * Add one member to a hub (POST /client/user/add). It runs through the
   * same pipeline as a bulk add and returns its job id; the outcome arrives
   * through the bulk-add job poller. `admin` is refused server-side unless
   * the caller holds hub_users:update (hub admins and owners). The account
   * itself is never made admin, only the hub membership.
   */
  async addMember(
    projectUuid: string,
    projectTitle: string,
    payload: AddMemberPayload,
  ): Promise<BulkCreateAndShareResponse> {
    const { data } = await this.client.post<{
      job_id: string
      status: string
    }>(
      '/client/user/add',
      {
        email: payload.email,
        admin: payload.admin,
        first_name: payload.first_name ?? '',
        last_name: payload.last_name ?? '',
        organization: payload.org ?? '',
        team: payload.team ?? '',
        project_title: projectTitle,
      },
      { headers: { [HUB_UUID_HEADER]: projectUuid } },
    )
    return { jobId: data.job_id, status: data.status }
  }

  /**
   * Dispatch a bulk-create-and-share background job for one hub.
   * Multipart upload: exactly one of `csv` or `emails_text` must be
   * provided. Returns immediately with a job_id; the result lands in
   * the bulk-user-access Slack channel via the background-task reporter.
   *
   * Role is hardcoded server-side to `viewer`.
   */
  async bulkCreateAndShare(
    payload: BulkCreateAndSharePayload,
  ): Promise<BulkCreateAndShareResponse> {
    const formData = new FormData()
    if (payload.csv !== null) {
      formData.append('csv', payload.csv)
    }
    if (payload.emailsText !== null && payload.emailsText.trim() !== '') {
      formData.append('emails_text', payload.emailsText)
    }
    formData.append('project_title', payload.projectTitle)
    // The target hub travels in the X-Hub-UUID header — the
    // authorization subject the scope authorizer resolves owner/scopes
    // against — not a request-body field.
    const { data } = await this.client.post<{
      job_id: string
      status: string
    }>('/client/user/bulk', formData, {
      headers: { [HUB_UUID_HEADER]: payload.projectUuid },
    })
    return {
      jobId: data.job_id,
      status: data.status,
    }
  }

  /**
   * Fetch the caller's v2 RBAC role for the given hub (the canonical
   * `GET /client/hub/user` endpoint). Used to gate role-restricted UI such
   * as the bulk add/share entry point. `hub_role` is one of
   * owner/admin/member/guest (never null — the backend fails closed to
   * `member` when unresolved).
   */
  async fetchHubRole(projectUuid: string): Promise<string> {
    const { data } = await this.client.get<{ hub_role: string }>(
      '/client/hub/user',
      { headers: { [HUB_UUID_HEADER]: projectUuid } },
    )
    return data.hub_role
  }

  /**
   * Poll the current state of a previously-dispatched bulk-add job.
   *
   * The backend always returns 200; the body's `status` field is
   * `'pending'` (still running OR Redis TTL elapsed) /
   * `'completed'` / `'failed'`. Callers dispatch on `status`. Any
   * unexpected axios error rethrows so the poller can decide whether
   * to retry or give up. (Previously the route returned 404 for
   * pending, which generated a per-poll INFO+traceback in prod logs;
   * the new protocol returns 200 + status field.)
   */
  async fetchBulkJobStatus(
    projectUuid: string,
    jobId: string,
  ): Promise<BulkJobStatusEntry | null> {
    try {
      const { data } = await this.client.get<BulkJobStatusEntry>(
        `/client/user/bulk/${encodeURIComponent(jobId)}/status`,
        { headers: { [HUB_UUID_HEADER]: projectUuid } },
      )
      return data
    } catch (err) {
      // Truly unexpected errors (5xx, network) bubble up; the poller
      // swallows in the interval tick so the next 5s poll retries.
      // Legacy 404-as-pending fallback kept for graceful migration
      // (the backend ALWAYS returns 200 now, but if a pre-deploy pod
      // is still serving the old 404 behavior, treat it as pending
      // instead of crashing).
      if (isAxiosError(err) && err.response?.status === 404) {
        handleException(err, {
          operation: 'settings.access.bulk-add.status',
          disposition: 'expected',
          isExpectedError: (error) =>
            isAxiosError(error) && error.response?.status === 404,
          reason: 'Legacy server reports a pending job as not found',
        })
        return null
      }
      throw err
    }
  }

  /**
   * Reset a hub member's login (owner-only). The backend deletes and
   * recreates the target's Cognito account, triggering a fresh
   * password-setup email. The target hub travels in the `X-Hub-UUID`
   * header — the authorization subject the scope authorizer resolves
   * owner/scopes against — mirroring `bulkCreateAndShare` / `fetchHubRole`.
   * The email of the member to reset is the request body.
   */
  async resetUser(projUuid: string, email: string): Promise<ResetUserResponse> {
    const { data } = await this.client.post<ResetUserResponse>(
      '/client/user/reset',
      { email },
      { headers: { [HUB_UUID_HEADER]: projUuid } },
    )
    return data
  }

  /**
   * Fetch the bulk-add-members CSV template from the backend. Returns a Blob
   * the caller can use with `URL.createObjectURL` to trigger a browser
   * download.
   */
  async fetchBulkAddMembersTemplate(): Promise<Blob> {
    const { data } = await this.client.get<Blob>(
      '/client/user/bulk-add-members-template',
      { responseType: 'blob' },
    )
    return data
  }
}
